// ListContact.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Container, Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import { toast, ToastContainer } from "react-toastify";
import SweetAlert from "react-bootstrap-sweetalert";
import { fetchContacts, deleteContact } from "../../../Redux/contactSlice";

function ListContact() {
  const [alert, setAlert] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useHistory();

  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i> {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i> {msg}
        </strong>,
      );
  };

  const columns = [
    {
      header: "ID",
      accessorKey: "id",
      size: 50,
    },
    {
      header: "Full Name",
      accessorKey: "fullName",
      Cell: ({ row }) => (
        <div>
          <strong>
            {row.original.firstName} {row.original.lastName}
          </strong>
        </div>
      ),
    },
    {
      header: "Email",
      accessorKey: "email",
    },
    {
      header: "Phone",
      accessorKey: "phone",
    },
    {
      header: "Subject",
      accessorKey: "subject",
    },
    {
      header: "Status",
      accessorKey: "status",
      Cell: ({ row }) => (
        <span
          className={`badge badge-${row.original.status === "read" ? "success" : "warning"}`}
        >
          {row.original.status || "unread"}
        </span>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      Cell: ({ row }) => (
        <div>{new Date(row.original.createdAt).toLocaleDateString()}</div>
      ),
    },
    {
      accessorKey: "id",
      header: "Actions",
      size: 120,
      Cell: ({ row }) => (
        <div className="actions-right block_action">
          <Button
            onClick={() => navigate.push(`/contact/detail/${row.original.id}`)}
            variant="info"
            size="sm"
            className="text-info btn-link"
            title="View Details"
          >
            <i className="fa fa-eye" />
          </Button>
          <Button
            onClick={() => confirmDelete(row.original.id)}
            variant="danger"
            size="sm"
            className="text-danger btn-link"
            title="Delete"
          >
            <i className="fa fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  const getContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchContacts());
      setContacts(response.payload || []);
    } catch (error) {
      notify(2, "Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const confirmDelete = (id) => {
    setAlert(
      <SweetAlert
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        subtitle="This contact message will be permanently deleted!"
        onConfirm={() => deleteContactMessage(id)}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="default"
        confirmBtnText="Yes, delete"
        cancelBtnText="Cancel"
        showCancel
      >
        You won't be able to revert this!
      </SweetAlert>,
    );
  };

  const deleteContactMessage = async (id) => {
    try {
      await dispatch(deleteContact(id));
      notify(1, "Contact message deleted successfully");
      getContacts();
      hideAlert();
    } catch (error) {
      notify(2, "Failed to delete contact message");
    }
  };

  const hideAlert = () => {
    setAlert(null);
  };

  useEffect(() => {
    getContacts();
  }, [getContacts]);

  // Stats summary component
  const StatsCards = () => {
    const total = contacts.length;
    const unread = contacts.filter((c) => c.status !== "read").length;
    const read = contacts.filter((c) => c.status === "read").length;

    return (
      <Row className="mb-4">
        <Col md="4">
          <Card className="text-white bg-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">Total Messages</h6>
                  <h2 className="mb-0">{total}</h2>
                </div>
                <i className="fas fa-envelope fa-2x opacity-50"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md="4">
          <Card className="text-white bg-warning">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">Unread</h6>
                  <h2 className="mb-0">{unread}</h2>
                </div>
                <i className="fas fa-envelope-open fa-2x opacity-50"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md="4">
          <Card className="text-white bg-success">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">Read</h6>
                  <h2 className="mb-0">{read}</h2>
                </div>
                <i className="fas fa-check-circle fa-2x opacity-50"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Container fluid>
        {alert}
        <ToastContainer />
        <Row>
          <Col md="12">
            <h4 className="title">Contact Messages</h4>
            <StatsCards />
            <Card>
              <Card.Body>
                <MaterialReactTable
                  columns={columns}
                  data={contacts}
                  state={{ isLoading: loading }}
                  enableColumnActions={true}
                  enableColumnFilters={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableBottomToolbar={true}
                  enableTopToolbar={true}
                  muiTableBodyRowProps={{ hover: true }}
                  positionToolbarAlertBanner="bottom"
                  initialState={{
                    sorting: [{ id: "createdAt", desc: true }],
                  }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ListContact;
